'use client';

import { Crown, Diamond, Medal, Shield } from '@phosphor-icons/react';
import type { LoyaltyLevel } from '@/lib/api/loyalty';

const TIER_CONFIG: Record<number, {
  gradient: string;
  icon: typeof Crown;
  label: string;
}> = {
  1: { gradient: 'from-[#C9956B] to-[#A67648]', icon: Shield, label: 'Bronze' },
  2: { gradient: 'from-[#A8B8C8] to-[#7D8FA0]', icon: Medal, label: 'Silver' },
  3: { gradient: 'from-[#D4A853] to-[#B8912A]', icon: Diamond, label: 'Gold' },
  4: { gradient: 'from-[#7B42FF] to-[#5A1FD6]', icon: Crown, label: 'Platinum' },
};

interface TierCardProps {
  level: LoyaltyLevel;
}

export function TierCard({ level }: TierCardProps) {
  const tier = TIER_CONFIG[level.current] || TIER_CONFIG[1];
  const Icon = tier.icon;
  const isMax = level.current === 4;
  const remaining = level.next_min ? level.next_min - level.lifetime_total : 0;

  return (
    <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${tier.gradient} p-5 md:p-6`}>
      {/* Subtle pattern overlay */}
      <div className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '24px 24px',
        }}
      />

      {/* Decorative glow */}
      <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-white/10 blur-2xl" />

      <div className="relative">
        {/* Header row */}
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <Icon className="w-5 h-5 text-white" weight="fill" />
            </div>
            <div>
              <p className="text-[11px] font-medium text-white/60 uppercase tracking-widest">
                {isMax ? 'Максимальный' : `Уровень ${level.current}`}
              </p>
              <h3 className="text-xl font-bold text-white font-heading leading-tight">
                {tier.label}
              </h3>
            </div>
          </div>

          <div className="text-right">
            <p className="text-3xl font-extrabold text-white font-heading leading-none">
              {level.earn_rate}%
            </p>
            <p className="text-[11px] font-medium text-white/60 mt-0.5">
              кэшбэк
            </p>
          </div>
        </div>

        {/* Progress */}
        {!isMax ? (
          <div>
            <div className="h-1.5 rounded-full bg-white/20 overflow-hidden">
              <div
                className="h-full rounded-full bg-white transition-all duration-700 ease-out"
                style={{ width: `${level.progress_percent}%` }}
              />
            </div>
            <div className="flex justify-between mt-2">
              <p className="text-xs text-white/60">
                {formatCurrency(level.lifetime_total)} из {formatCurrency(level.next_min!)}
              </p>
              <p className="text-xs font-semibold text-white/80">
                До {level.next_name}: {formatCurrency(remaining)}
              </p>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 mt-1">
            <Crown className="w-4 h-4 text-white/70" weight="fill" />
            <p className="text-xs font-medium text-white/70">
              Максимальный уровень достигнут
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function formatCurrency(value: number): string {
  return value.toLocaleString('ru-RU') + ' ₽';
}
